import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import BaseInput from "../../../../BaseComponents/BaseInput";
import BackButton from "../../../../BaseComponents/BackButton";
import BaseButton from "../../../../BaseComponents/BaseButton";
import { validationMessages } from "../../../../Constant/validation";
import { PlaceHolderFormat } from "../../../../Constant/requireMessage";
import { holidayLabel } from "../../../../Constant/HR_HolidayTemplate/holidayTemplate";
import { notFound } from "../../../../Constant";
import { holidayTitle } from "../../../../Constant/title";
import { toast } from "react-toastify";
import {
  checkStatusCodeSuccess,
  extractYearMonth,
  extractYearWiseDate,
  isRequired,
  maxDate,
  minDate,
  yearMonthOnly,
} from "../../../../Constant/common";
import {
  addHoliday,
  deleteHoliday,
  editHoliday,
  viewHolidayTemplate,
} from "../../../../Api/HolidayTemplate";
import BaseModal from "../../../../BaseComponents/BaseModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelectedCenter } from "../../../CenterContext";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import moment from "moment";
import { Plus, Trash2, Calendar, AlertCircle } from "react-feather";

const CreateHolidayTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [holidayStart, setHolidayStart] = useState(null);
  const [holidayEnd, setHolidayEnd] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [holidayIndexToDelete, setHolidayIndexToDelete] = useState(null);
  const [dltBtnLoader, setDltBtnLoader] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const { selectedCenterPayload } = useSelectedCenter();
  const { setLoading } = usePageLoader();

  useEffect(() => {
    document.title = holidayTitle;
  }, []);

  const validationSchema = Yup.object({
    templateName: Yup.string().required(validationMessages.required(holidayLabel.templateName)),
    holidayStart: Yup.string().required(validationMessages.required(holidayLabel.startMonth)),
    holidayEnd: Yup.string().required(validationMessages.required(holidayLabel.endMonth)),
    holidays: Yup.array().of(
      Yup.object({
        holiday_name: Yup.string().required(validationMessages.required(holidayLabel.holidayName)),
        holiday_date: Yup.string()
          .required(validationMessages.required(holidayLabel.holidayDate))
          .test(
            "is-valid-date",
            `${holidayLabel.dateValidation} ${extractYearWiseDate(holidayStart)} and ${extractYearWiseDate(holidayEnd)}`,
            (value) => {
              const date = new Date(value);
              return !holidayStart || !holidayEnd
                ? true
                : date >= new Date(holidayStart) && date <= new Date(holidayEnd);
            },
          ),
      }),
    ),
  });

  const formik = useFormik({
    initialValues: {
      templateName: "",
      holidayStart: "",
      holidayEnd: "",
      holidays: [{ holiday_name: "", holiday_date: "" }],
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const startMoment = extractYearMonth(values.holidayStart);
      const endMoment = extractYearMonth(values.holidayEnd);
      const payload = {
        ...selectedCenterPayload,
        template_name: values.templateName,
        from_date: yearMonthOnly(startMoment),
        to_date: yearMonthOnly(endMoment),
        holidays: values.holidays,
      };
      setBtnLoader(true);
      const apiCall = id ? editHoliday(id, payload) : addHoliday(payload);
      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            resetForm();
            navigate("/holidayTemplate");
          } else toast.error(resp?.message);
        })
        .catch((err) => {
          if (Array.isArray(err?.response?.data?.message))
            err?.response?.data?.message?.forEach((m) => toast.error(m));
          else toast.error(err?.response?.data?.message || err?.message);
        })
        .finally(() => setBtnLoader(false));
    },
  });

  const { setValues } = formik;

  const fetchData = useCallback(
    (fetchId) => {
      if (!fetchId) return;
      setLoading(true);
      viewHolidayTemplate(fetchId)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            const d = resp?.data;
            setValues({
              templateName: d?.template_name,
              holidayStart: d?.from_date,
              holidayEnd: d?.to_date,
              holidays: d?.holidayDetail,
            });
            const sm = moment(d?.from_date, "YYYY-MM");
            const em = moment(d?.to_date, "YYYY-MM");
            setHolidayStart(sm.isValid() ? sm.startOf("month").toDate() : null);
            setHolidayEnd(em.isValid() ? em.endOf("month").toDate() : null);
          } else toast.error(resp?.message);
        })
        .catch((err) => err)
        .finally(() => setLoading(false));
    },
    [setValues, setLoading],
  );

  useEffect(() => {
    fetchData(id);
  }, [fetchData, id]);

  const confirmDelete = () => {
    setDltBtnLoader(true);
    const updatedHolidays = [...formik.values.holidays];
    const toDelete = updatedHolidays[holidayIndexToDelete];
    if (toDelete.id) {
      deleteHoliday(toDelete.id, { is_deleted: true })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            toast.success(resp?.message);
            fetchData(id);
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message))
        .finally(() => {
          setIsModalOpen(false);
          setDltBtnLoader(false);
        });
    } else {
      updatedHolidays.splice(holidayIndexToDelete, 1);
      formik.setValues({ ...formik.values, holidays: updatedHolidays });
      setIsModalOpen(false);
      setDltBtnLoader(false);
    }
  };

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {id ? holidayLabel.editHolidayTemplateTitle : holidayLabel.holidayTemplateTitle}
          </h1>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
            {id ? "Update template details and holidays" : "Create a new holiday calendar template"}
          </p>
        </div>
        <BackButton onClick={() => navigate("/holidayTemplate")} />
      </div>

      <form
        onSubmit={formik.handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        {/* Template settings card */}
        <div
          className="animate-slide-up"
          style={{
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 20,
            overflow: "hidden",
          }}
        >
          <div style={{ height: 3, background: "linear-gradient(90deg,#f59e0b,#f97316)" }} />
          <div style={{ padding: "20px 24px" }}>
            <h2
              style={{
                margin: "0 0 16px",
                fontSize: "0.9375rem",
                fontWeight: 700,
                color: "var(--text-primary)",
              }}
            >
              Template Settings
            </h2>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "start" }}>
              {/* Template Name — fixed width */}
              <div style={{ flex: "0 0 220px", minWidth: 180 }}>
                <BaseInput
                  label={holidayLabel.templateName}
                  type="text"
                  name={holidayLabel.templateNameKey}
                  placeholder={PlaceHolderFormat(holidayLabel.templateName)}
                  value={formik.values.templateName}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.templateName}
                  touched={formik.touched.templateName}
                  required
                />
              </div>

              {/* Period pickers — grow to fill remaining space, pickers side-by-side */}
              <div style={{ flex: "1 1 320px", minWidth: 280 }} className="mb-3">
                <label
                  className="form-label"
                  style={{ fontWeight: 600, display: "block", marginBottom: 6 }}
                >
                  {holidayLabel.annualHolidayPeriod} {isRequired}
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 120px", minWidth: 120 }}>
                    <DatePicker
                      selected={holidayStart}
                      onChange={(date) => {
                        const sm = moment(date).startOf("month");
                        const startVal = sm.isValid() ? sm.format("YYYY-MM") : "";
                        const maxEnd = sm.clone().add(11, "months").endOf("month");
                        const endVal = maxEnd.isValid() ? maxEnd.format("YYYY-MM") : "";

                        setHolidayStart(sm.isValid() ? sm.toDate() : null);
                        setHolidayEnd(maxEnd.isValid() ? maxEnd.toDate() : null);

                        formik.setFieldValue(holidayLabel.holidayStart, startVal, false);
                        formik.setFieldValue(holidayLabel.holidayEnd, endVal, false);
                        setTimeout(() => {
                          formik.validateForm();
                        }, 0);
                      }}
                      dateFormat="yyyy-MM"
                      showMonthYearPicker
                      className="form-control"
                      placeholderText={PlaceHolderFormat(holidayLabel.startMonth)}
                      minDate={minDate}
                      maxDate={maxDate}
                      popperProps={{ strategy: "fixed" }}
                      popperPlacement="bottom-start"
                      portalId="root"
                    />
                    {formik.touched.holidayStart && formik.errors.holidayStart && (
                      <div className="invalid-feedback d-block">{formik.errors.holidayStart}</div>
                    )}
                  </div>

                  <span
                    style={{
                      color: "var(--text-muted)",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      flexShrink: 0,
                    }}
                  >
                    {holidayLabel.To}
                  </span>

                  <div style={{ flex: "1 1 120px", minWidth: 120 }}>
                    <DatePicker
                      selected={holidayEnd}
                      onChange={(date) => {
                        const em = moment(date).endOf("month");
                        const endVal = em.isValid() ? em.format("YYYY-MM") : "";
                        setHolidayEnd(em.isValid() ? em.toDate() : null);
                        formik.setFieldValue(holidayLabel.holidayEnd, endVal, false);
                        setTimeout(() => {
                          formik.validateForm();
                        }, 0);
                      }}
                      dateFormat="yyyy-MM"
                      showMonthYearPicker
                      className="form-control"
                      placeholderText={PlaceHolderFormat(holidayLabel.endMonth)}
                      minDate={holidayStart || minDate}
                      maxDate={
                        holidayStart
                          ? moment(holidayStart).add(11, "months").endOf("month").toDate()
                          : maxDate
                      }
                      disabled={!holidayStart}
                      popperProps={{ strategy: "fixed" }}
                      popperPlacement="bottom-start"
                      portalId="root"
                    />
                    {formik.touched.holidayEnd && formik.errors.holidayEnd && (
                      <div className="invalid-feedback d-block">{formik.errors.holidayEnd}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Holiday list card */}
        <div
          className="animate-slide-up"
          style={{
            animationDelay: "60ms",
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 20,
            overflow: "hidden",
          }}
        >
          <div style={{ height: 3, background: "linear-gradient(90deg,#6366f1,#8b5cf6)" }} />
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid var(--glass-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: "rgba(99,102,241,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Calendar size={15} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                {holidayLabel.holidayList}
                <span
                  style={{
                    marginLeft: 8,
                    padding: "2px 8px",
                    borderRadius: 999,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    background: "rgba(99,102,241,0.1)",
                    color: "var(--brand-indigo)",
                  }}
                >
                  {formik.values.holidays?.length}
                </span>
              </h2>
            </div>
            <button
              type="button"
              onClick={() =>
                formik.setFieldValue(holidayLabel.holidays, [
                  ...formik.values.holidays,
                  { holiday_name: "", holiday_date: "", isNew: true },
                ])
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 14px",
                borderRadius: 9,
                background: "var(--brand-indigo)",
                border: "none",
                color: "#fff",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <Plus size={14} strokeWidth={2.5} /> Add Holiday
            </button>
          </div>

          {formik.values.holidays?.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "48px 20px",
                gap: 10,
              }}
            >
              <AlertCircle size={36} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
              <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {formik.values.holidays.map((holiday, index) => (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr auto",
                    gap: 16,
                    padding: "14px 24px",
                    alignItems: "start",
                    borderBottom:
                      index < formik.values.holidays.length - 1
                        ? "1px solid var(--glass-border)"
                        : "none",
                  }}
                >
                  <BaseInput
                    type="text"
                    label={holidayLabel.holidayName}
                    name={`holidays.${index}.holiday_name`}
                    placeholder={PlaceHolderFormat(holidayLabel.holidayName)}
                    value={holiday.holiday_name}
                    handleChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors?.holidays?.[index]?.holiday_name}
                    touched={formik.touched?.holidays?.[index]?.holiday_name}
                    required
                  />

                  {/* DatePicker for holiday date */}
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: 600 }}>
                      {holidayLabel.holidayDate} <span style={{ color: "#f43f5e" }}>*</span>
                    </label>
                    <DatePicker
                      selected={holiday.holiday_date ? new Date(holiday.holiday_date) : null}
                      onChange={(date) =>
                        formik.setFieldValue(
                          `holidays.${index}.holiday_date`,
                          date ? moment(date).format("YYYY-MM-DD") : "",
                        )
                      }
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                      placeholderText={PlaceHolderFormat(holidayLabel.holidayDate)}
                      minDate={holidayStart || undefined}
                      maxDate={holidayEnd || undefined}
                      popperProps={{ strategy: "fixed" }}
                      popperPlacement="bottom-start"
                      portalId="root"
                    />
                    {formik.touched?.holidays?.[index]?.holiday_date &&
                      formik.errors?.holidays?.[index]?.holiday_date && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.holidays[index].holiday_date}
                        </div>
                      )}
                  </div>

                  <div className="mb-3">
                    <label
                      className="form-label"
                      style={{ display: "block", visibility: "hidden", marginBottom: 6 }}
                    >
                      &nbsp;
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setHolidayIndexToDelete(index);
                        setIsModalOpen(true);
                      }}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        border: "1px solid var(--glass-border)",
                        background: "rgba(244,63,94,0.08)",
                        color: "#f43f5e",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <Trash2 size={14} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingBottom: 8 }}>
          <BaseButton color="cancel" type="button" onClick={() => navigate("/holidayTemplate")}>
            Cancel
          </BaseButton>
          <BaseButton color="primary" type="submit" loader={btnLoader} disabled={btnLoader}>
            {holidayLabel.Save}
          </BaseButton>
        </div>
      </form>

      <BaseModal
        isOpen={isModalOpen}
        title={holidayLabel.deleteConfirmation}
        toggler={() => setIsModalOpen(false)}
        submit={confirmDelete}
        submitText={holidayLabel.Yes}
        loader={dltBtnLoader}
        disabled={dltBtnLoader}
      >
        <p style={{ margin: 0, color: "var(--text-primary)" }}>{holidayLabel.deleteMessage}</p>
      </BaseModal>
    </div>
  );
};

export default CreateHolidayTemplate;
